import { forwardRef, useId } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { HelpIcon } from "@/components/shared/HelpIcon";
import { ErrorTextComponent } from "../error-text-component";
import { HelperTextComponent } from "../helper-text-component";
import type { GhostDiff } from "./autocomplete/types";

interface FormTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  tooltip?: boolean;
  tooltipMessage?: React.ReactNode;
  /**
   * Ghost text simples: exibe um complemento cinza após o cursor (estilo VS Code).
   * Não usar junto com `ghostDiff`.
   */
  ghostText?: string;
  ghostBadge?: string;
  /**
   * Modo diff: exibe uma barra de sugestão abaixo da textarea mostrando
   * o que será removido (vermelho riscado) e o que entrará (verde).
   * Não usar junto com `ghostText`.
   */
  ghostDiff?: GhostDiff;
}

export const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  (
    {
      label,
      error,
      required,
      helperText,
      className,
      tooltip,
      tooltipMessage,
      ghostText,
      ghostBadge,
      ghostDiff,
      value,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const hasGhostText = !!ghostText;
    const hasGhostDiff = !!ghostDiff;

    // Mesma lacuna do FormMoneyInput: sem fallback, `htmlFor` ficava sem par quando o caller
    // não passava `id` — o que é o caso comum. `FormInput`/`FormPercentInput` já geram o deles.
    const geradoPorId = useId();
    const inputId = props.id ?? geradoPorId;

    return (
      <div className="space-y-2">
        {/* Label + tooltip */}
        {(label || tooltip) && (
          <div className="flex h-[20px] items-center gap-2">
            {label && (
              <Label
                htmlFor={inputId}
                className={cn("text-sm font-medium text-foreground", error && "text-destructive")}
              >
                {required && <span className="mr-1 text-destructive">*</span>}
                {label}
              </Label>
            )}

            {tooltip && <HelpIcon tooltipMessage={tooltipMessage} />}
          </div>
        )}

        {/* Textarea + ghost text overlay */}
        <div className="relative">
          <Textarea
            ref={ref}
            id={inputId}
            value={value}
            placeholder={hasGhostText && !value ? "" : placeholder}
            className={cn(
              "z-10 bg-transparent transition-colors",
              error && "border-destructive focus-visible:ring-destructive",
              hasGhostText && "pr-16",
              className,
            )}
            {...props}
          />

          {/* Ghost text simples: espaçador invisível + complemento cinza */}
          {hasGhostText && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 z-0 overflow-hidden px-3 py-2 text-base md:text-sm"
            >
              <span className="invisible whitespace-pre-wrap break-words">{value}</span>
              <span className="whitespace-pre text-muted-foreground/50">{ghostText}</span>
              {ghostBadge && (
                <>
                  <span className="mx-2 text-xs text-muted-foreground/40">·</span>
                  <span className="text-xs text-muted-foreground/50">{ghostBadge}</span>
                </>
              )}
            </div>
          )}

          {/* Badge Tab ⇥ (modo ghost text) */}
          {hasGhostText && <TabBadge className="absolute right-3 top-2 z-20" />}
        </div>

        {/* Barra de diff — aparece abaixo da textarea no modo fuzzy */}
        {hasGhostDiff && <GhostDiffBar diff={ghostDiff} />}

        <ErrorTextComponent error={error} />
        <HelperTextComponent text={helperText} error={error} />
      </div>
    );
  },
);

FormTextarea.displayName = "FormTextarea";

// ─── Sub-componentes internos ────────────────────────────────────────────────

function TabBadge({ className }: { className?: string }) {
  return (
    <div className={cn("pointer-events-none", className)}>
      <kbd className="inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] leading-none text-muted-foreground shadow-sm">
        Tab ⇥
      </kbd>
    </div>
  );
}

function GhostDiffBar({ diff }: { diff: GhostDiff }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-1.5 text-xs">
      {/* Texto que será removido — vermelho com riscado */}
      <span className="shrink-0 text-destructive line-through">{diff.deleted}</span>

      <span className="shrink-0 text-muted-foreground">→</span>

      {/* Texto que entrará — verde */}
      <span className="shrink-0 text-green-600 dark:text-green-400">{diff.inserted}</span>

      {/* Badge opcional */}
      {diff.badge && (
        <>
          <span className="mx-1 text-muted-foreground/40">·</span>
          <span className="truncate text-muted-foreground/60">{diff.badge}</span>
        </>
      )}

      {/* Tab badge à direita */}
      <TabBadge className="ml-auto" />
    </div>
  );
}
